import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiMail, FiShield, FiKey, FiArrowRight, FiX, FiRefreshCw } from 'react-icons/fi';
import { Modal } from './Modal';
import { Button } from './Button';
import { useI18n } from '../i18n/useI18n';
import { getEmailCaptcha, requestEmailBindCode, bindEmail } from '../utils/api';

export function EmailBindModal({ open, onClose, onSuccess, pushToast }) {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [loadingCaptcha, setLoadingCaptcha] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const canSend = useMemo(() => !sending && cooldown === 0, [sending, cooldown]);

  const loadCaptcha = useCallback(async () => {
    try {
      setLoadingCaptcha(true);
      const res = await getEmailCaptcha();
      if (res?.code === 0) {
        setCaptchaId(res.data?.captcha_id || '');
        setCaptchaImage(res.data?.image || '');
      } else {
        pushToast?.(res?.msg || t('emailBind.captchaFailed'), 'error');
      }
    } catch (error) {
      pushToast?.(error.message || t('emailBind.captchaFailed'), 'error');
    } finally {
      setLoadingCaptcha(false);
    }
  }, [pushToast, t]);

  useEffect(() => {
    if (open) {
      setEmail('');
      setCaptchaId('');
      setCaptchaImage('');
      setCaptchaCode('');
      setEmailCode('');
      setCooldown(0);
      loadCaptcha();
    }
  }, [open, loadCaptcha]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => {
      setCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const handleSendCode = async () => {
    if (!email) {
      pushToast?.(t('emailBind.emailRequired'), 'info');
      return;
    }
    if (!captchaCode) {
      pushToast?.(t('emailBind.captchaRequired'), 'info');
      return;
    }
    try {
      setSending(true);
      const res = await requestEmailBindCode({
        email,
        captchaId,
        captchaCode,
        lang
      });
      if (res?.code === 0) {
        pushToast?.(t('emailBind.sendSuccess'), 'success');
        setCooldown(60);
      } else {
        pushToast?.(res?.msg || t('emailBind.sendFailed'), 'error');
      }
    } catch (error) {
      pushToast?.(error.message || t('emailBind.sendFailed'), 'error');
    } finally {
      setSending(false);
      loadCaptcha();
      setCaptchaCode('');
    }
  };

  const handleBind = async () => {
    if (!emailCode) {
      pushToast?.(t('emailBind.codeRequired'), 'info');
      return;
    }
    try {
      setVerifying(true);
      const res = await bindEmail({ email, code: emailCode, lang });
      if (res?.code === 0) {
        pushToast?.(t('emailBind.bindSuccess'), 'success');
        onSuccess?.(res.data);
        onClose?.();
      } else {
        pushToast?.(res?.msg || t('emailBind.bindFailed'), 'error');
      }
    } catch (error) {
      pushToast?.(error.message || t('emailBind.bindFailed'), 'error');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidthClass="max-w-md"
      showHeader={false}
    >
      <div className="relative">
        {/* 顶部装饰 */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-sky-500 to-indigo-500 rounded-t-2xl" />

        {/* 右上角关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center bg-slate-900/60 border border-slate-600/60 text-slate-300 rounded-3xl cursor-pointer transition-all duration-200 hover:border-sky-500 hover:text-sky-100 z-10"
          aria-label="关闭弹窗"
        >
          ×
        </button>

        {/* 标题区域 */}
        <div className="pt-6 pb-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-sky-500/20 border border-indigo-500/30 mb-4">
            <FiMail className="w-6 h-6 text-indigo-400" />
          </div>
          <div className="text-xs font-medium text-indigo-400 uppercase tracking-widest mb-2">
            Email Binding
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            {t('emailBind.title')}
          </h2>
          <p className="text-sm text-slate-400">
            {t('emailBind.desc')}
          </p>
        </div>

        {/* 表单区域 */}
        <div className="space-y-5">
          {/* 邮箱输入 */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
              <FiMail className="w-3.5 h-3.5 text-indigo-400" />
              {t('emailBind.emailLabel')}
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('emailBind.emailPlaceholder')}
                className="w-full bg-slate-900/60 border border-slate-700/60 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/20 transition-all"
              />
              <div className="absolute inset-0 rounded-xl pointer-events-none border border-transparent focus-within:border-indigo-500/30" />
            </div>
          </div>

          {/* 图片验证码 */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
              <FiShield className="w-3.5 h-3.5 text-indigo-400" />
              {t('emailBind.captchaLabel')}
            </label>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <input
                  value={captchaCode}
                  onChange={(e) => setCaptchaCode(e.target.value)}
                  placeholder={t('emailBind.captchaPlaceholder')}
                  className="w-full bg-slate-900/60 border border-slate-700/60 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/20 transition-all"
                />
              </div>
              <button
                type="button"
                onClick={loadCaptcha}
                disabled={loadingCaptcha}
                className="shrink-0 w-28 h-12 rounded-xl bg-slate-800 border border-slate-700/60 flex items-center justify-center overflow-hidden hover:border-slate-600 transition-colors"
                title={t('emailBind.captchaRefresh')}
              >
                {loadingCaptcha ? (
                  <FiRefreshCw className="w-4 h-4 text-slate-500 animate-spin" />
                ) : (
                  captchaImage && (
                    <img 
                      src={captchaImage} 
                      alt="captcha" 
                      className="w-full h-full object-contain"
                    />
                  )
                )}
              </button>
            </div>
          </div>

          {/* 邮箱验证码 */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-400 uppercase tracking-wider">
              <FiKey className="w-3.5 h-3.5 text-indigo-400" />
              {t('emailBind.codeLabel')}
            </label>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <input
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value)}
                  placeholder={t('emailBind.codePlaceholder')}
                  className="w-full bg-slate-900/60 border border-slate-700/60 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/20 transition-all"
                />
              </div>
              <button
                onClick={handleSendCode}
                disabled={!canSend}
                className={`
                  shrink-0 px-4 h-12 rounded-xl text-sm font-medium transition-all
                  ${cooldown > 0 
                    ? 'bg-slate-800 text-slate-500 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-indigo-500 to-sky-500 text-white hover:from-indigo-400 hover:to-sky-400 shadow-lg shadow-indigo-500/20'
                  }
                `}
              >
                {cooldown > 0 ? `${cooldown}s` : t('emailBind.sendCode')}
              </button>
            </div>
          </div>
        </div>

        {/* 按钮区域 */}
        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-slate-400 bg-slate-800/60 border border-slate-700/60 hover:bg-slate-800 hover:text-slate-300 transition-all"
          >
            {t('emailBind.cancel')}
          </button>
          <button
            onClick={handleBind}
            disabled={verifying}
            className="flex-1 px-4 py-3 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-sky-500 hover:from-indigo-400 hover:to-sky-400 shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {verifying ? (
              <>
                <FiRefreshCw className="w-4 h-4 animate-spin" />
                {t('emailBind.binding')}
              </>
            ) : (
              <>
                {t('emailBind.confirm')}
                <FiArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>

        {/* 底部安全提示 */}
        <div className="mt-6 pt-4 border-t border-slate-800">
          <p className="text-xs text-slate-500 text-center">
            {t('emailBind.securityTip') || '您的邮箱将用于账户安全验证和重要通知'}
          </p>
        </div>
      </div>
    </Modal>
  );
}
